import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { deleteCampaign, getCampaigns } from '../../../../../api/endpoints/cmsContent';
import type { Campaign } from '../../../../../api/types/cmsContent';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../../components/common';
import { CampaignFormDialog } from '../CampaignFormDialog';

/** `SiteDetailPage` içinde bir sekme; `siteId` sabit prop. `GetCampaignById` yok. */
export default function CampaignListPage({ siteId }: { siteId: number }) {
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);

  const query = useQuery({ queryKey: ['cms', 'content', 'campaigns', siteId], queryFn: () => getCampaigns(siteId) });
  const rows: Campaign[] = useMemo(() => query.data?.airCampaigns ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.airlineName, row.airlineCode, row.from, row.to, row.link]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteCampaign(id),
    invalidateKeys: [['cms', 'content', 'campaigns']],
    successMessage: 'Kampanya silindi.',
  });
  const deletingName = rows.find((r) => r.id === del.target)?.airlineName ?? '';

  const columns: DataTableColumn<Campaign>[] = [
    {
      id: 'airline',
      header: 'Havayolu',
      cell: (r) => `${r.airlineName ?? '—'} ${r.airlineCode ? `(${r.airlineCode})` : ''}`.trim(),
    },
    { id: 'from', header: 'Kalkış', cell: (r) => r.from ?? '—' },
    { id: 'to', header: 'Varış', cell: (r) => r.to ?? '—' },
    {
      id: 'price',
      header: 'Fiyat',
      cell: (r) => (r.price !== undefined ? `${r.price} ${r.priceCurrency ?? ''}`.trim() : '—'),
    },
    {
      id: 'range',
      header: 'Tarih Aralığı',
      cell: (r) => `${r.startDate ? r.startDate.slice(0, 10) : '—'} — ${r.endDate ? r.endDate.slice(0, 10) : '—'}`,
    },
  ];

  return (
    <ListPageScaffold
      title="Kampanyalar"
      headerVariant="section"
      createLabel="Yeni Kampanya"
      onCreate={() => setCreateOpen(true)}
      search={{ value: search, onChange: setSearch, placeholder: 'Havayolu, kalkış, varış, link...' }}
      query={query}
      rows={filteredRows}
      columns={columns}
      getRowKey={(r) => r.id}
      emptyTitle={rows.length === 0 ? 'Kayıtlı kampanya bulunamadı.' : 'Aramayla eşleşen kampanya bulunamadı.'}
      rowActions={(r) => <RowActions onEdit={() => setEditing(r)} onDelete={() => del.request(r.id)} />}
    >
      <CampaignFormDialog open={createOpen} campaign={null} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <CampaignFormDialog open={!!editing} campaign={editing} siteId={siteId} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Kampanyayı sil"
        description={`"${deletingName}" kampanyasını silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
